import { useHandler } from "@redotech/react-util/hook";
import {
  UpsClaimInfo,
  UpsDamageInfo,
  UpsDamageLocation,
  UpsItemCondition,
  UpsPackageType,
  UpsPackagingMaterial,
  UpsSealMethod,
} from "@redotech/redo-package-protection-common/shipping-claims/carrier-claim-info/ups";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoTextareaInputField } from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";

const YES_NO_OPTIONS: RedoListItem<boolean>[] = [
  { type: "text", text: "Yes", value: true, id: "yes" },
  { type: "text", text: "No", value: false, id: "no" },
];

function enumOptions<T extends string>(values: T[]): RedoListItem<T>[] {
  return values.map((v) => ({ type: "text", text: v, value: v, id: v }));
}

const LOCATION_OPTIONS = enumOptions(Object.values(UpsDamageLocation));
const ITEM_CONDITION_OPTIONS = enumOptions(Object.values(UpsItemCondition));
const PACKAGE_TYPE_OPTIONS = enumOptions(Object.values(UpsPackageType));
const SEAL_METHOD_OPTIONS = enumOptions(Object.values(UpsSealMethod));
const PACKAGING_MATERIAL_OPTIONS = enumOptions(
  Object.values(UpsPackagingMaterial),
);

export type PartialUpsDamageInfo = Partial<UpsDamageInfo>;

export function isUpsDamageInfoComplete(
  d: PartialUpsDamageInfo,
): d is UpsDamageInfo {
  if (
    d.contentsAndPackagingAvailable === undefined ||
    !d.dateDamageDiscovered ||
    !d.totalItemsInPackage ||
    d.itemsMissing === undefined ||
    d.containsDangerousGoods === undefined ||
    d.canBeSealedSafely === undefined ||
    !d.currentLocation ||
    !d.itemCondition ||
    !d.packageType ||
    d.damageToOutside === undefined ||
    !d.howPackageSealed ||
    !d.packagingMaterials
  ) {
    return false;
  }
  if (
    d.currentLocation === UpsDamageLocation.AT_OTHER &&
    (!d.otherLocationContactName ||
      !d.otherLocationCity ||
      !d.otherLocationPostal ||
      !d.otherLocationCountry)
  ) {
    return false;
  }
  if (d.itemCondition === UpsItemCondition.OTHER && !d.itemConditionOther) {
    return false;
  }
  if (d.packageType === UpsPackageType.OTHER && !d.packageTypeOther) {
    return false;
  }
  if (
    d.packagingMaterials === UpsPackagingMaterial.OTHER &&
    !d.packagingMaterialsOther
  ) {
    return false;
  }
  return true;
}

export function buildUpsClaimInfo(
  d: PartialUpsDamageInfo,
): UpsClaimInfo | null {
  if (!isUpsDamageInfoComplete(d)) {
    return null;
  }
  return { damage: d };
}

interface UpsClaimInfoSectionProps {
  value: PartialUpsDamageInfo;
  setValue: (next: PartialUpsDamageInfo) => void;
}

export const UpsClaimInfoSection = memo(function UpsClaimInfoSection({
  value,
  setValue,
}: UpsClaimInfoSectionProps) {
  const update = useHandler((patch: Partial<UpsDamageInfo>) => {
    setValue({ ...value, ...patch });
  });

  const dateString = useMemo(
    () =>
      value.dateDamageDiscovered
        ? formatDateForInput(value.dateDamageDiscovered)
        : "",
    [value.dateDamageDiscovered],
  );

  return (
    <Flex dir="column" gap="md">
      <Text fontSize="sm" fontWeight="semibold">
        UPS damage details
      </Text>

      <YesNoField
        label="Are both contents and packaging available for inspection?"
        setValue={(v) => update({ contentsAndPackagingAvailable: v })}
        value={value.contentsAndPackagingAvailable}
      />

      <RedoTextInput
        inputTypeHint="date"
        label="Date damage was discovered"
        setValue={(s) => {
          if (!s) {
            update({ dateDamageDiscovered: undefined });
            return;
          }
          const d = new Date(s);
          if (!isNaN(d.getTime())) {
            update({ dateDamageDiscovered: d });
          }
        }}
        value={dateString}
      />

      <RedoTextInput
        inputTypeHint="number"
        label="Total items in the package"
        max={9999}
        min={1}
        setValue={(s) => {
          const n = parseInt(s, 10);
          update({ totalItemsInPackage: isNaN(n) ? undefined : n });
        }}
        value={
          value.totalItemsInPackage !== undefined
            ? String(value.totalItemsInPackage)
            : ""
        }
      />

      <YesNoField
        label="Are items missing from the package?"
        setValue={(v) => update({ itemsMissing: v })}
        value={value.itemsMissing}
      />

      <YesNoField
        label="Contains dangerous goods?"
        setValue={(v) => update({ containsDangerousGoods: v })}
        value={value.containsDangerousGoods}
      />

      <YesNoField
        label="Can the package be sealed and transported safely?"
        setValue={(v) => update({ canBeSealedSafely: v })}
        value={value.canBeSealedSafely}
      />

      <RedoSingleSelectDropdownInput
        label="Current location of the damaged package"
        options={LOCATION_OPTIONS}
        optionSelected={(item) => update({ currentLocation: item.item.value })}
        selectedItem={LOCATION_OPTIONS.find(
          (o) => o.value === value.currentLocation,
        )}
      />

      {value.currentLocation === UpsDamageLocation.AT_OTHER && (
        <Flex dir="column" gap="sm">
          <RedoTextInput
            label="Other location: contact name"
            maxLength={35}
            setValue={(s) => update({ otherLocationContactName: s })}
            value={value.otherLocationContactName ?? ""}
          />
          <RedoTextInput
            label="Other location: city"
            maxLength={50}
            setValue={(s) => update({ otherLocationCity: s })}
            value={value.otherLocationCity ?? ""}
          />
          <RedoTextInput
            label="Other location: postal code"
            maxLength={8}
            setValue={(s) => update({ otherLocationPostal: s })}
            value={value.otherLocationPostal ?? ""}
          />
          <RedoTextInput
            label="Other location: country code"
            maxLength={2}
            setValue={(s) => update({ otherLocationCountry: s })}
            value={value.otherLocationCountry ?? ""}
          />
        </Flex>
      )}

      <RedoSingleSelectDropdownInput
        label="Condition of damaged item(s)"
        options={ITEM_CONDITION_OPTIONS}
        optionSelected={(item) => update({ itemCondition: item.item.value })}
        selectedItem={ITEM_CONDITION_OPTIONS.find(
          (o) => o.value === value.itemCondition,
        )}
      />

      {value.itemCondition === UpsItemCondition.OTHER && (
        <RedoTextareaInputField
          label="Describe the item condition"
          maxLength={50}
          setValue={(s) => update({ itemConditionOther: s })}
          value={value.itemConditionOther ?? ""}
        />
      )}

      <RedoSingleSelectDropdownInput
        label="Package type"
        options={PACKAGE_TYPE_OPTIONS}
        optionSelected={(item) => update({ packageType: item.item.value })}
        selectedItem={PACKAGE_TYPE_OPTIONS.find(
          (o) => o.value === value.packageType,
        )}
      />

      {value.packageType === UpsPackageType.OTHER && (
        <RedoTextInput
          label="Describe the package type"
          maxLength={50}
          setValue={(s) => update({ packageTypeOther: s })}
          value={value.packageTypeOther ?? ""}
        />
      )}

      <YesNoField
        label="Damage to outside of package?"
        setValue={(v) => update({ damageToOutside: v })}
        value={value.damageToOutside}
      />

      <RedoSingleSelectDropdownInput
        label="How was the package sealed?"
        options={SEAL_METHOD_OPTIONS}
        optionSelected={(item) => update({ howPackageSealed: item.item.value })}
        selectedItem={SEAL_METHOD_OPTIONS.find(
          (o) => o.value === value.howPackageSealed,
        )}
      />

      <RedoSingleSelectDropdownInput
        label="Packaging materials"
        options={PACKAGING_MATERIAL_OPTIONS}
        optionSelected={(item) =>
          update({ packagingMaterials: item.item.value })
        }
        selectedItem={PACKAGING_MATERIAL_OPTIONS.find(
          (o) => o.value === value.packagingMaterials,
        )}
      />

      {value.packagingMaterials === UpsPackagingMaterial.OTHER && (
        <RedoTextInput
          label="Describe the packaging materials"
          maxLength={50}
          setValue={(s) => update({ packagingMaterialsOther: s })}
          value={value.packagingMaterialsOther ?? ""}
        />
      )}
    </Flex>
  );
});

interface YesNoFieldProps {
  label: string;
  value: boolean | undefined;
  setValue: (next: boolean) => void;
}

const YesNoField = memo(function YesNoField({
  label,
  value,
  setValue,
}: YesNoFieldProps) {
  return (
    <RedoSingleSelectDropdownInput
      label={label}
      options={YES_NO_OPTIONS}
      optionSelected={(item) => setValue(item.item.value)}
      selectedItem={YES_NO_OPTIONS.find((o) => o.value === value)}
    />
  );
});

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function formatDateForInput(d: Date): string {
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}
